import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Monitor, Smartphone, PhoneCall, Download, Copy, Check, Loader2 } from 'lucide-react';
import { api } from '../lib/spizirnaUi';

/**
 * Nastavení → Ke stažení.
 *
 * Instalačky všech našich aplikací na jednom místě: FofrLama POS (pokladna
 * a kuchyňský displej, Windows), FofrTelefon (telefon bistra, Android) a
 * FofrApp (aplikace pro zaměstnance, Android). U aplikací do telefonu je QR
 * kód — naskenuješ ho telefonem a stáhne se rovnou tam.
 *
 * Instaluje se ručně jen poprvé; další verze si POS a FofrTelefon berou
 * samy (Správa → Provozovna → Pokladny a telefon), FofrApp se aktualizuje balíčkem.
 */
export default function KeStazeni({ isDark }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState('');

    useEffect(() => {
        api('/api/ke-stazeni').then(setData).catch(() => setChyba('Seznam se nepodařilo načíst.'));
    }, []);

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const kartaBg = isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)';

    if (chyba) return <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>;
    if (!data) return <Loader2 size={18} className="animate-spin" style={{ color: muted }} />;

    const spolecne = { muted, border, kartaBg, isDark };

    return (
        <div className="space-y-4 max-w-3xl">
            <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Ke stažení</h3>
                <p className="text-xs mt-1" style={{ color: muted }}>
                    Instalačky našich aplikací. Ručně se instalují jen poprvé — další verze si pokladna i telefon bistra stáhnou samy
                    (Správa → Provozovna → Pokladny a telefon), FofrApp se aktualizuje sám.
                </p>
            </div>

            <Aplikace {...spolecne} Ikona={Monitor} barva="#FF8A00" nazev="FofrLama POS"
                popis="Pokladna a kuchyňský displej · Windows 10/11 · instaluje se pro uživatele, bez administrátorských práv"
                navod="Při prvním spuštění se Windows SmartScreen zeptá (instalátor není podepsaný): Další informace → Přesto spustit. Pak v pokladně spáruj terminál kódem z Správa → Provozovna → Pokladny a telefon."
                verze={data.pos} />

            <Aplikace {...spolecne} Ikona={PhoneCall} barva="#10b981" nazev="FofrTelefon" qr
                popis="Telefon bistra · Android 10 a novější · hlásí hovory a SMS do pokladny"
                navod="Stáhni v telefonu (QR kód), povol instalaci z prohlížeče, v aplikaci zadej kód telefonu z Správa → Provozovna → Pokladny a telefon a projdi povolení. SMS: Informace o aplikaci → ⋮ → Povolit omezená nastavení."
                verze={data.telefon} />

            <Aplikace {...spolecne} Ikona={Smartphone} barva="#3b82f6" nazev="FofrApp" qr
                popis="Aplikace pro zaměstnance · Android · Cockpit, směny, rozvoz s GPS na pozadí"
                navod="Stáhni v telefonu zaměstnance (QR kód), povol instalaci z prohlížeče a přihlas se PINem."
                verze={data.app ? { stabilni: { ...data.app, verze: data.app.balicek ? `obsah ${data.app.balicek}` : null } } : null} />
        </div>
    );
}

function Aplikace({ Ikona, barva, nazev, popis, navod, verze, qr, muted, border, kartaBg }) {
    const polozky = [verze?.stabilni && { ...verze.stabilni, test: false }, verze?.testovaci && { ...verze.testovaci, test: true }].filter(Boolean);

    return (
        <div className="rounded-xl px-4 py-4 space-y-3" style={{ background: kartaBg, border: `1px solid ${border}` }}>
            <div className="flex items-start gap-3">
                <div className="flex items-center justify-center flex-shrink-0 rounded-xl"
                    style={{ width: 40, height: 40, background: `${barva}22`, color: barva }}>
                    <Ikona size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{nazev}</div>
                    <div className="text-xs mt-0.5" style={{ color: muted }}>{popis}</div>
                </div>
            </div>

            {polozky.length === 0 ? (
                <p className="text-xs" style={{ color: muted }}>Zatím žádná vydaná verze.</p>
            ) : polozky.map((v, i) => <Verze key={i} v={v} qr={qr} barva={barva} muted={muted} border={border} />)}

            <p className="text-[11px] leading-relaxed" style={{ color: muted }}>{navod}</p>
        </div>
    );
}

function Verze({ v, qr, barva, muted, border }) {
    const [obrazek, setObrazek] = useState(null);
    const [zkopirovano, setZkopirovano] = useState(false);

    useEffect(() => {
        if (qr) QRCode.toDataURL(v.url, { margin: 1, width: 220, errorCorrectionLevel: 'M' }).then(setObrazek).catch(() => {});
    }, [qr, v.url]);

    const kopirovat = async () => {
        try { await navigator.clipboard.writeText(v.url); setZkopirovano(true); setTimeout(() => setZkopirovano(false), 2000); } catch { /* bez schránky nic */ }
    };

    return (
        <div className="flex flex-wrap items-center gap-3 rounded-lg px-3 py-2.5" style={{ border: `1px solid ${border}` }}>
            {obrazek && <img src={obrazek} alt="QR kód ke stažení" width={88} height={88} className="rounded bg-white p-1" />}
            <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2 flex-wrap">
                    {v.verze && <span className="font-mono text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{v.verze}</span>}
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: 'transparent', border: `1px solid ${border}`, color: v.test ? 'var(--text-secondary)' : '#10b981' }}>
                        {v.test ? 'testovací' : 'aktuální'}
                    </span>
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: muted }}>
                    {(v.velikost / 1048576).toFixed(1)} MB
                    {v.datum ? ` · ${new Date(v.datum).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                    {v.poznamka ? ` · ${v.poznamka}` : ''}
                </div>
            </div>
            <button onClick={kopirovat} title="Zkopírovat odkaz"
                className="flex items-center gap-1 text-xs rounded-lg px-2.5 py-2"
                style={{ background: 'transparent', border: `1px solid ${border}`, color: muted, cursor: 'pointer' }}>
                {zkopirovano ? <Check size={13} /> : <Copy size={13} />} {zkopirovano ? 'Zkopírováno' : 'Odkaz'}
            </button>
            <a href={v.url} className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2 no-underline"
                style={{ background: barva, color: '#fff' }}>
                <Download size={13} /> Stáhnout
            </a>
        </div>
    );
}
